import { FiatKycPosition } from '../FiatKycPosition'
import type { Surface } from '@funkit/connect-core'
import { View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { logger } from '../../logging/logger'
import { useTheme } from '../../theme/restyle'
import { isDarkColor } from '../../theme/isDarkColor'
import { FiatBackButton } from '../FiatBackButton'
import type { SurfaceAdapter, SurfaceContext } from './adapter'
import { SumsubKycSurface } from './SumsubKycSurface'

interface SumsubParams {
  accessToken: string
}

/** Mints a replacement token when the SDK's own runs out mid-capture. */
export type RenewToken = () => Promise<string | undefined>

/** The applicant token this surface names, or `undefined` when it is not one. */
function readSumsubKycParams(surface: Surface): SumsubParams | undefined {
  if (surface.kind !== 'KYC_SDK' || surface.provider !== 'SUMSUB') {
    return undefined
  }
  // The contract requires `params`, but no response is validated before it
  // reaches here, so an absent one must fail the match rather than throw.
  const params: unknown = surface.params
  if (params === null || typeof params !== 'object') {
    return undefined
  }
  const { accessToken } = params as Record<string, unknown>
  if (typeof accessToken !== 'string' || accessToken.length === 0) {
    return undefined
  }
  return { accessToken }
}

/**
 * Sumsub collects identity in its own SDK, full-screen.
 *
 * `HANDOFF` rather than `EMBEDDED`: document capture and liveness own the
 * canvas and draw their own chrome, so there is no widget for the screen to
 * cover and no readiness to wait for.
 */
/**
 * Built per screen rather than registered, because renewal needs the quote the
 * capture belongs to — which the surface itself does not carry.
 */
export function sumsubKycAdapter(renewToken: RenewToken): SurfaceAdapter {
  return (surface) => {
    const params = readSumsubKycParams(surface)
    if (params === undefined) {
      return undefined
    }
    return { control: 'HANDOFF', render: renderer(params, renewToken) }
  }
}

/** The registry's entry, for a screen that cannot renew. */
export const sumsubKyc: SurfaceAdapter = sumsubKycAdapter(() =>
  Promise.resolve(undefined),
)

const renderer =
  (params: SumsubParams, renewToken: RenewToken) =>
  ({ onClose, onFailed, report }: SurfaceContext) => (
    <SumsubKycScreen
      params={params}
      renewToken={renewToken}
      onClose={onClose}
      onFailed={onFailed}
      report={report}
    />
  )

/**
 * The sheet does not constrain its children, so `flex: 1` collapses to nothing
 * and the surface renders an empty screen. The whole window is claimed and its
 * top inset paid, exactly as the status screen — the other handoff — does.
 */
function SumsubKycScreen({
  params,
  renewToken,
  onClose,
  onFailed,
  report,
}: {
  params: SumsubParams
  renewToken: RenewToken
} & Pick<SurfaceContext, 'onClose' | 'onFailed' | 'report'>) {
  const { height } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const theme = useTheme()
  const modal = theme.background.modal

  return (
    <View
      style={{
        height,
        paddingTop: insets.top,
        backgroundColor: theme.background.modal,
      }}
    >
      {/* A handoff draws its own chrome, and the SDK draws none of ours — so
          without this the flow has no way out but killing the app. */}
      <FiatBackButton onPress={onClose} />
      <FiatKycPosition position={3} />
      <SumsubKycSurface
        accessToken={params.accessToken}
        // The SDK draws light unless told; a light identity flow inside a dark
        // app reads as a different application.
        scheme={isDarkColor(modal) ? 'dark' : 'light'}
        onRenewToken={renewToken}
        // The surface yields nothing (`expects` is unset), so submission is
        // the whole signal. Whether the checks passed is the flow's to poll.
        onSubmitted={() => {
          logger.info('connect-rn:fiat:sumsub_kyc_submitted')
          report({})
        }}
        onFailed={(code) => {
          logger.warn('connect-rn:fiat:sumsub_kyc_failed', { code })
          onFailed(code)
        }}
      />
    </View>
  )
}
